// components/Testimonials.js
import React, { useState, useEffect } from 'react';
import './Testimonials.css';

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTestimonial, setNewTestimonial] = useState({
    author: '',
    role: '',
    text: '',
    rating: 5,
    company: '',
    verified: true
  });

  // Default testimonials data
  const defaultTestimonials = [
    {
      id: 'default1',
      text: "LeadVala doubled our weekly leads and the quality was night-and-day better than before. The ROI has been incredible!",
      author: "Rajesh Kumar",
      role: "Real Estate Agency Owner",
      company: "Kumar Realty",
      rating: 5,
      verified: true,
      createdAt: new Date('2024-01-15')
    },
    {
      id: 'default2',
      text: "The AI + Human verification process ensures we only speak to serious prospects. Our conversion rate has increased by 150%!",
      author: "Priya Sharma",
      role: "Education Consultant",
      company: "EduSuccess Academy",
      rating: 5,
      verified: true,
      createdAt: new Date('2024-01-20')
    },
    {
      id: 'default3',
      text: "Finally, a lead generation service that delivers what it promises. Every lead is properly qualified and ready to convert.",
      author: "Amit Patel",
      role: "Healthcare Clinic Director",
      company: "HealthFirst Clinic",
      rating: 5,
      verified: true,
      createdAt: new Date('2024-01-25')
    },
    {
      id: 'default4',
      text: "We've tried multiple lead generation services before, but LeadVala's 7-layer verification system is unmatched. Quality over quantity!",
      author: "Sneha Gupta",
      role: "Marketing Director",
      company: "TechFlow Solutions",
      rating: 5,
      verified: true,
      createdAt: new Date('2024-02-01')
    },
    {
      id: 'default5',
      text: "The WhatsApp delivery system is so convenient. We get leads directly and can respond immediately. Game changer for our business!",
      author: "Vikram Singh",
      role: "Business Owner",
      company: "Singh Interiors",
      rating: 5,
      verified: true,
      createdAt: new Date('2024-02-10')
    },
    {
      id: 'default6',
      text: "Professional team, transparent process, and results that speak for themselves. LeadVala has become our go-to lead partner.",
      author: "Meera Joshi",
      role: "Founder",
      company: "Wellness Clinic Network",
      rating: 5,
      verified: true,
      createdAt: new Date('2024-02-15')
    }
  ];

  // Load testimonials on component mount
  useEffect(() => {
    const loadTestimonials = () => {
      setLoading(true);
      
      // Simulate loading delay
      setTimeout(() => {
        // Get testimonials from localStorage or use defaults
        const storedTestimonials = localStorage.getItem('leadvala_testimonials');
        if (storedTestimonials) {
          const parsed = JSON.parse(storedTestimonials);
          // Combine stored testimonials with defaults, remove duplicates
          const combined = [...parsed, ...defaultTestimonials];
          const unique = combined.filter((testimonial, index, self) => 
            index === self.findIndex(t => t.id === testimonial.id)
          );
          setTestimonials(unique.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
        } else {
          setTestimonials(defaultTestimonials);
        }
        setLoading(false);
      }, 1000);
    };

    loadTestimonials();
  }, []);

  // Save testimonials to localStorage
  const saveTestimonialsToStorage = (updatedTestimonials) => {
    const userTestimonials = updatedTestimonials.filter(t => !t.id.startsWith('default'));
    localStorage.setItem('leadvala_testimonials', JSON.stringify(userTestimonials));
  };

  // Add new testimonial
  const handleAddTestimonial = (e) => {
    e.preventDefault();
    
    const testimonialData = {
      ...newTestimonial,
      id: `user_${Date.now()}`,
      createdAt: new Date(),
      approved: true, // Auto-approve for demo purposes
      featured: false
    };

    const updatedTestimonials = [testimonialData, ...testimonials];
    setTestimonials(updatedTestimonials);
    saveTestimonialsToStorage(updatedTestimonials);
    
    // Reset form
    setNewTestimonial({
      author: '',
      role: '',
      text: '',
      rating: 5,
      company: '',
      verified: true
    });
    setShowAddForm(false);
    
    // Show success message
    alert('Thank you for your testimonial! It has been added successfully.');
  };

  // Update testimonial
  const handleUpdateTestimonial = (id, updatedData) => {
    const updatedTestimonials = testimonials.map(testimonial =>
      testimonial.id === id 
        ? { ...testimonial, ...updatedData, updatedAt: new Date() }
        : testimonial
    );
    setTestimonials(updatedTestimonials);
    saveTestimonialsToStorage(updatedTestimonials);
  };

  // Delete testimonial (for user-added testimonials only)
  const handleDeleteTestimonial = (id) => {
    if (id.startsWith('default')) {
      alert('Cannot delete default testimonials');
      return;
    }
    
    const updatedTestimonials = testimonials.filter(testimonial => testimonial.id !== id);
    setTestimonials(updatedTestimonials);
    saveTestimonialsToStorage(updatedTestimonials);
  };

  // Render loading state
  if (loading) {
    return (
      <section id="testimonials" className="testimonials-section fade-in-section">
        <div className="container">
          <h2 className="section-title">Client Success Stories</h2>
          <div className="testimonials-loading">
            <div className="loader"></div>
            <p>Loading testimonials...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="testimonials" className="testimonials-section fade-in-section">
      <div className="container">
        <h2 className="section-title">Client Success Stories</h2>
        <p className="section-subtitle">
          See what our clients have to say about their experience with LeadVala
        </p>
        
        <div className="testimonials-grid">
          {testimonials.slice(0, 6).map((testimonial, index) => (
            <div 
              key={testimonial.id} 
              className="testimonial-card"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {testimonial.verified && (
                <div className="verified-badge">
                  <span>✓</span> Verified Client
                </div>
              )}
              
              <div className="stars">
                {[...Array(testimonial.rating || 5)].map((_, i) => (
                  <span key={i} className="star">⭐</span>
                ))}
              </div>
              
              <p className="testimonial-text">"{testimonial.text}"</p>
              
              <div className="testimonial-author">
                <div className="author-avatar">
                  {testimonial.author.charAt(0).toUpperCase()}
                </div>
                <div className="author-info">
                  <strong>{testimonial.author}</strong>
                  <span>{testimonial.role}</span>
                  {testimonial.company && (
                    <span className="company">{testimonial.company}</span>
                  )}
                </div>
              </div>

              {/* Delete button for user-added testimonials */}
              {!testimonial.id.startsWith('default') && (
                <button 
                  className="delete-testimonial"
                  onClick={() => handleDeleteTestimonial(testimonial.id)}
                  title="Delete testimonial"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Add Testimonial Button */}
        <div className="add-testimonial-section">
          <button 
            className="add-testimonial-btn"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            Share Your Success Story
          </button>
        </div>

        {/* Add Testimonial Form (Modal) */}
        {showAddForm && (
          <div className="testimonial-modal">
            <div className="modal-content">
              <button 
                className="close-modal"
                onClick={() => setShowAddForm(false)}
              >
                ×
              </button>
              
              <h3>Share Your Experience</h3>
              
              <form onSubmit={handleAddTestimonial}>
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Your Name"
                    value={newTestimonial.author}
                    onChange={(e) => setNewTestimonial({
                      ...newTestimonial,
                      author: e.target.value
                    })}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Your Role"
                    value={newTestimonial.role}
                    onChange={(e) => setNewTestimonial({
                      ...newTestimonial,
                      role: e.target.value
                    })}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Company Name (Optional)"
                    value={newTestimonial.company}
                    onChange={(e) => setNewTestimonial({
                      ...newTestimonial,
                      company: e.target.value
                    })}
                  />
                </div>
                
                <div className="form-group">
                  <label>Rating:</label>
                  <div className="rating-selector">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className={`star-btn ${newTestimonial.rating >= star ? 'active' : ''}`}
                        onClick={() => setNewTestimonial({
                          ...newTestimonial,
                          rating: star
                        })}
                      >
                        ⭐
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="form-group">
                  <textarea
                    placeholder="Share your experience with LeadVala..."
                    value={newTestimonial.text}
                    onChange={(e) => setNewTestimonial({
                      ...newTestimonial,
                      text: e.target.value
                    })}
                    rows="4"
                    required
                  />
                </div>
                
                <button type="submit" className="submit-testimonial-btn">
                  Submit Testimonial
                </button>
              </form>
            </div>
          </div>
        )}

        {/* View All Testimonials */}
        {testimonials.length > 6 && (
          <div className="view-all-section">
            <button className="view-all-btn">
              View All {testimonials.length} Testimonials
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;